import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Users, TrendingUp, TrendingDown, DollarSign, Building2,
  ArrowUp, ArrowDown, Minus, BarChart3, Target, Award,
  Lock, Eye, EyeOff, Shield, Zap, AlertTriangle, CheckCircle,
  Globe, Server, Database, Cloud, Sparkles, Info
} from 'lucide-react';
import { useDemoData } from '../components/useDemoData';
import EmptyDataState from '../components/EmptyDataState';

const PeerBenchmark = () => {
  const { shouldShowData } = useDemoData();
  // Iter84al — ping real backend on mount; auth-validated.
  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/finops/v2/peer-benchmark/snapshot`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }).catch(() => {});
  }, []);
  const [showDetails, setShowDetails] = useState(true);
  
  const companyProfile = {
    industry: 'SaaS / Technology',
    size: '100-500 employees',
    cloudSpend: '$850K/month',
    providers: ['AWS', 'Snowflake']
  };

  const benchmarks = [
    {
      metric: 'Total Cloud Spend',
      yourValue: 850000,
      peerMedian: 720000,
      peerTop25: 580000,
      unit: '/month',
      status: 'above',
      recommendation: 'Your spend is 18% above peer median. Top performers spend 32% less.'
    },
    {
      metric: 'Compute Cost %',
      yourValue: 52,
      peerMedian: 45,
      peerTop25: 38,
      unit: '%',
      status: 'above',
      recommendation: 'Consider rightsizing or using Spot/Preemptible instances.'
    },
    {
      metric: 'Database Cost %',
      yourValue: 28,
      peerMedian: 30,
      peerTop25: 25,
      unit: '%',
      status: 'good',
      recommendation: 'Your database spending is well optimized.'
    },
    {
      metric: 'Cost per Employee',
      yourValue: 2833,
      peerMedian: 2400,
      peerTop25: 1800,
      unit: '/emp',
      status: 'above',
      recommendation: 'Consolidate tools and optimize resource allocation.'
    },
    {
      metric: 'Reserved Coverage',
      yourValue: 45,
      peerMedian: 60,
      peerTop25: 75,
      unit: '%',
      status: 'below',
      recommendation: 'Increase reserved instance coverage for predictable workloads.'
    },
    {
      metric: 'Waste Score',
      yourValue: 12,
      peerMedian: 15,
      peerTop25: 8,
      unit: '%',
      status: 'good',
      recommendation: 'Your waste is below median but top performers achieve <8%.'
    }
  ];

  const peerInsights = [
    { title: '73% of peers use Spot instances', desc: 'Average savings: 65%', icon: Zap },
    { title: '68% have automated scaling', desc: 'Reduces over-provisioning', icon: Server },
    { title: '82% use reserved instances', desc: 'For production databases', icon: Database },
    { title: '45% have FinOps team', desc: 'Dedicated cost management', icon: Users }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'above': return 'text-red-600 bg-red-50';
      case 'below': return 'text-amber-600 bg-amber-50';
      case 'good': return 'text-emerald-600 bg-emerald-50';
      default: return 'text-slate-400 bg-slate-50';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'above': return <ArrowUp className="w-4 h-4" />;
      case 'below': return <ArrowDown className="w-4 h-4" />;
      case 'good': return <CheckCircle className="w-4 h-4" />;
      default: return <Minus className="w-4 h-4" />;
    }
  };

  const formatValue = (value, unit) => {
    if (unit === '/month' || unit === '/emp') {
      return `$${value.toLocaleString()}`;
    }
    return `${value}${unit}`;
  };

  const potentialSavings = benchmarks
    .filter(b => b.status === 'above')
    .reduce((sum, b) => {
      if (b.metric === 'Total Cloud Spend') return sum + (b.yourValue - b.peerMedian);
      return sum;
    }, 0);

  return (
    <div className="space-y-6" data-testid="peer-benchmark-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Users className="h-7 w-7 text-emerald-500" />
            Peer Benchmark
          </h1>
          <p className="text-slate-400 dark:text-gray-400 mt-1">
            See how your cloud costs compare to similar companies (anonymized)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-emerald-100 text-emerald-700">
            <Lock className="w-3 h-3 mr-1" />
            Anonymous
          </Badge>
          {shouldShowData && (
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowDetails(!showDetails)}
          >
            {showDetails ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
            {showDetails ? 'Hide' : 'Show'} Details
          </Button>
          )}
        </div>
      </div>

      {!shouldShowData ? (
        <EmptyDataState title="Peer Benchmark" description="Connect your cloud providers to see how your cloud costs compare to similar companies in your industry." />
      ) : (
      <>

      {/* Company Profile Card — v34.44 — emerald to match brand palette (was indigo blue) */}
      <Card className="bg-emerald-700 text-white border-0 shadow-lg shadow-emerald-900/20" data-testid="profile-card">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-emerald-100 text-[11px] font-semibold uppercase tracking-widest mb-1">Your Profile</p>
              <h3 className="text-xl font-bold text-white">{companyProfile.industry}</h3>
              <p className="text-emerald-100 text-sm font-medium">{companyProfile.size} · {companyProfile.cloudSpend}</p>
              <div className="flex gap-2 mt-3">
                {companyProfile.providers.map((p) => (
                  <Badge key={p} className="bg-emerald-900/50 ring-1 ring-white/30 text-white border-0 font-semibold">{p}</Badge>
                ))}
              </div>
            </div>
            <div className="text-right">
              <p className="text-emerald-100 text-[11px] font-semibold uppercase tracking-widest mb-1">Comparing against</p>
              <p className="text-3xl font-extrabold text-white tabular-nums">247</p>
              <p className="text-emerald-100 text-sm font-medium">similar companies</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Potential Savings Alert */}
      {potentialSavings > 0 && (
        <Card className="border-2 border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-emerald-500 flex items-center justify-center">
                  <Target className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-emerald-800 dark:text-emerald-200">
                    Potential to save ${(potentialSavings).toLocaleString()}/month
                  </h3>
                  <p className="text-sm text-emerald-600 dark:text-emerald-400">
                    By reaching peer median performance
                  </p>
                </div>
              </div>
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white">
                View Recommendations
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Benchmark Cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {benchmarks.map((benchmark, idx) => (
          <Card key={idx} className="hover:shadow-lg transition-shadow">
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-4">
                <h3 className="font-semibold text-slate-900 dark:text-white">{benchmark.metric}</h3>
                <Badge className={getStatusColor(benchmark.status)}>
                  {getStatusIcon(benchmark.status)}
                  <span className="ml-1">
                    {benchmark.status === 'above' ? 'Above Median' : 
                     benchmark.status === 'below' ? 'Below Target' : 'On Track'}
                  </span>
                </Badge>
              </div>

              {/* Visual Comparison */}
              <div className="space-y-3 mb-4">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-400">You</span>
                    <span className="font-semibold">{formatValue(benchmark.yourValue, benchmark.unit)}</span>
                  </div>
                  <div className="h-3 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full ${
                        benchmark.status === 'good' ? 'bg-emerald-500' : 
                        benchmark.status === 'above' ? 'bg-red-500' : 'bg-amber-500'
                      }`}
                      style={{ width: `${Math.min((benchmark.yourValue / benchmark.peerMedian) * 50, 100)}%` }}
                    />
                  </div>
                </div>
                
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-400">Peer Median</span>
                    <span className="font-medium text-slate-400">{formatValue(benchmark.peerMedian, benchmark.unit)}</span>
                  </div>
                  <div className="h-3 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-500 rounded-full" style={{ width: '50%' }} />
                  </div>
                </div>
                
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-400">Top 25%</span>
                    <span className="font-medium text-emerald-600">{formatValue(benchmark.peerTop25, benchmark.unit)}</span>
                  </div>
                  <div className="h-3 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-emerald-500 rounded-full"
                      style={{ width: `${(benchmark.peerTop25 / benchmark.peerMedian) * 50}%` }}
                    />
                  </div>
                </div>
              </div>

              {showDetails && (
                <div className="pt-3 border-t border-slate-200 dark:border-slate-700">
                  <p className="text-xs text-slate-400 flex items-start gap-1">
                    <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    {benchmark.recommendation}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Peer Insights */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" />
            What Top Performers Do Differently
          </CardTitle>
          <CardDescription>Best practices from companies in the top 25%</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {peerInsights.map((insight, idx) => (
              <div key={idx} className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                <div className="w-10 h-10 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center mb-3">
                  <insight.icon className="w-5 h-5" />
                </div>
                <h4 className="font-medium text-slate-900 dark:text-white text-sm mb-1">{insight.title}</h4>
                <p className="text-xs text-slate-400">{insight.desc}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Privacy Note */}
      <Card className="bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-slate-900 dark:text-white mb-1">Your Privacy is Protected</h4>
              <p className="text-sm text-slate-400">
                All benchmark data is anonymized and aggregated. No company can identify your specific data, 
                and you cannot identify any other company. We only show statistical comparisons based on 
                industry, company size, and cloud usage patterns.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
      </>
      )}
    </div>
  );
};

export default PeerBenchmark;
